import { useState, useRef, useEffect } from 'react'
import { ScrollView, View, TextInput, Platform, Text, StyleSheet } from 'react-native'
import { BackButton } from '@/src/common/components/BackButton'
import { Routes } from '@/src/common/enums/routes'
import { OnboardingWrapper } from '@/src/common/wrappers/OnboardingWrapper'
import { Input, InputPhone } from '@/src/common/components/Input'
import { Button } from '@/src/common/components/Button'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  SEND_PHONE_VERIFICATION_CODE_FOR_NEW_ACCOUNT,
  UPDATE_USER_INFORMATION,
} from '@/src/common/gql/mutations/user'
import { useRouter } from 'expo-router'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useUserStore } from '@/src/common/store/useUserStore'
import { triggerSegmentIdentify } from '@/src/services/segment'
import { validateAccountCreation } from '@/src/services/forms'
import { openBrowserAsync } from 'expo-web-browser'
import { parsePhoneValue } from '@/src/common/utils/input'
import { parsePhoneNumber } from 'libphonenumber-js'
import { formatPhoneNumberForBackend } from '@/src/common/utils/formatPhoneNumberForBackend'
import { type SendPhoneVerificationCodeForNewAccountMutation } from '@api/mesa/graphql'
import { useTrackAccountCreationPage } from '@/src/common/hooks/useTrackAccountCreationPage'
import type { userInformationInterface } from '@/src/common/interfaces/user'
import { CHECK_PHONE_NUMBER_IS_AVAILABLE } from '@/src/common/gql/queries/user'
import { useSignout } from '@/src/common/hooks/useSignout'
import { AuthenticationE2E } from '@/src/experiences/authentication/e2e'
import { useSardine } from '@/src/services/Sardine'
import { usePartnerNameCodeStore } from '@/src/common/store/usePartnerNameCodeStore'
import { useAsyncStorage } from '@react-native-async-storage/async-storage'
import { mesaColorNames, typography } from '@/src/common/theme'
import { useMediaQuery } from 'react-responsive'

function launchPrivacyPage() {
  void openBrowserAsync('https://www.mesamember.com/legal/privacy-policy')
}

function launchTermsPage() {
  void openBrowserAsync('https://mesamember.com/legal/terms-of-service')
}

function PrivacyAndTerms() {
  return (
    <Text>
      By continuing, I agree with Mesa's{' '}
      <Text style={styles.linkStyle} onPress={launchPrivacyPage}>
        Privacy Policy
      </Text>{' '}
      and{' '}
      <Text style={styles.linkStyle} onPress={launchTermsPage}>
        Terms of Service
      </Text>
    </Text>
  )
}

export default function AccountCreation() {
  useTrackAccountCreationPage()
  const logError = useLogError()
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { setUserInformation, countryCode, setParsedPhoneNumber } = useUserStore()
  const router = useRouter()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const lastNameInputRef = useRef<TextInput>(null)
  const phoneInputRef = useRef<TextInput>(null)
  const [updateUserInformation] = useMutation(UPDATE_USER_INFORMATION)
  const [errors, setErrors] =
    useState<Partial<Awaited<ReturnType<typeof validateAccountCreation>>>>()
  const [checkPhoneNumberIsAvailable] = useLazyQuery(CHECK_PHONE_NUMBER_IS_AVAILABLE)
  const [sendVerificationCodeForNewAccount, { error }] = useMutation(
    SEND_PHONE_VERIFICATION_CODE_FOR_NEW_ACCOUNT,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { updateOptions, submitData } = useSardine()
  const { partnerNameCode } = usePartnerNameCodeStore()
  const { getItem: getPartnerNameCode } = useAsyncStorage('partner_name')

  const [
    [sendPhoneVerificationCodeError, phoneNumberUsedWhenOtpErrorReceived] = [],
    setSendPhoneVerificationCodeError,
  ] =
    useState<
      [
        sendPhoneVerificationCodeError: Extract<
          SendPhoneVerificationCodeForNewAccountMutation['sendPhoneVerificationCodeForNewAccount'],
          { __typename?: 'SendPhoneVerificationCodeError' }
        >,
        phoneNumberUsedWhenOtpErrorReceived: typeof phoneNumber,
      ]
    >()

  useEffect(() => {
    void updateOptions({ flow: 'account-creation-name-phone' })
  }, [updateOptions])

  function formatPhoneNumberToDisplayToUser() {
    try {
      const parsedPhoneNumber = parsePhoneValue(phoneNumber)
      setPhoneNumber(parsedPhoneNumber)
      const withoutCountryCode = parsePhoneNumber(phoneNumber, {
        defaultCountry: 'US',
      }).formatNational()
      setPhoneNumber(withoutCountryCode)
      return withoutCountryCode
    } catch {
      return phoneNumber
    }
  }

  async function handleSubmit() {
    try {
      void submitData()
      setErrors(undefined)
      setIsSubmitting(true)
      const phoneNumber = formatPhoneNumberToDisplayToUser()
      const formValid = validateAccountCreation({ firstName, lastName, phoneNumber })
      setErrors(formValid)

      if (
        formValid.firstName != undefined ||
        formValid.lastName != undefined ||
        formValid.phoneNumber != undefined
      ) {
        return
      }

      const phoneNumberToSendToBackend = formatPhoneNumberForBackend(phoneNumber, countryCode)

      const {
        data: { checkPhoneNumberIsAvailable: { phoneNumberIsAvailable = false } = {} } = {},
      } = await checkPhoneNumberIsAvailable({
        variables: { phoneNumber: phoneNumberToSendToBackend },
      })

      if (!phoneNumberIsAvailable) {
        setErrors({
          phoneNumber:
            'This phone number is already taken by a Mesa Member. Please go back to sign in or use a different phone number.',
        })
        return
      }

      const sourcePartnerName = partnerNameCode ?? (await getPartnerNameCode()) ?? undefined

      await updateUserInformation({
        variables: { firstName, lastName, sourcePartnerName },
        onCompleted: ({ updateUserInformation: user }) => {
          const userInformation = {
            ...user,
            firstName: user.firstName ?? undefined,
            lastName: user.lastName ?? undefined,
            phoneNumber: user.phoneNumber ?? undefined,
            email: user.email,
            nickname: user.nickname ?? undefined,
            lastLogin: user.lastLogin ?? undefined,
            createdAt: user.createdAt,
            updatedAt: user.updatedAt,
            picture: user.picture ?? undefined,
            sourcePartnerName: user.sourcePartnerName ?? undefined,
            metadata: {
              userTrackingFlag: user.metadata?.userTrackingFlag ?? undefined,
            },
            cardInformation: user.cardInformation
              ? {
                  ...user.cardInformation,
                  lastFour: String(user.cardInformation.lastFour),
                }
              : undefined,
          } as const satisfies userInformationInterface
          void triggerSegmentIdentify({ userId: user.id, userInformation })

          // Save the user info into userStore
          setUserInformation(userInformation)

          // persist it to state
          setParsedPhoneNumber(parsePhoneValue(phoneNumber))

          // Request OTP
          void (async () => {
            const { data } = await sendVerificationCodeForNewAccount({
              variables: { phoneNumber: phoneNumberToSendToBackend },
            })

            switch (data?.sendPhoneVerificationCodeForNewAccount.__typename) {
              case 'SendPhoneVerificationCodeSuccess':
                router.push(Routes.AccountCreationVerifyPhoneOtp.route)
                break
              case 'SendPhoneVerificationCodeError':
                setSendPhoneVerificationCodeError([
                  data.sendPhoneVerificationCodeForNewAccount,
                  phoneNumber,
                ])
                break
              default:
                logError('No useful data returned from server when sending phone OTP code')
                break
            }
          })()
        },
      })
    } catch (error) {
      logError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const signout = useSignout()

  const isFixedWidthViewport = useMediaQuery({ minWidth: 720 })

  return (
    <OnboardingWrapper paddingHorizontal={15}>
      <ScrollView
        style={Platform.OS === 'web' && { overflow: 'visible' }}
        contentContainerStyle={{ width: isFixedWidthViewport ? 500 : '100%' }}
      >
        <BackButton
          href="/get-started"
          show
          onPress={(e) => {
            e.preventDefault()
            signout()
          }}
        />
        <View style={styles.headerText}>
          <Text style={typography['heading/heading1']}>Create An Account</Text>
        </View>
        <View>
          <Text style={[styles.instructionText, typography['paragraph/md']]}>
            Let's get to know one another
          </Text>
        </View>
        <View style={{ gap: 10 }}>
          <Input
            autoComplete="given-name"
            autoCorrect={false}
            inputContainerStyle={{ paddingBottom: 0, marginBottom: 0 }}
            containerStyle={styles.inputStyle}
            segmentAction={'User Inputs First Name'}
            placeholder="First Name"
            sardineId="firstName"
            onChangeText={setFirstName}
            info={{ error: errors?.firstName }}
            onSubmitEditing={() => lastNameInputRef.current?.focus()}
            returnKeyType="next"
            testID={AuthenticationE2E.Ids.Input.FirstName}
          />
          <Input
            autoComplete="family-name"
            autoCorrect={false}
            inputContainerStyle={{ paddingBottom: 0, marginBottom: 0 }}
            containerStyle={styles.inputStyle}
            segmentAction={'User Inputs Last Name'}
            placeholder="Last Name"
            sardineId="lastName"
            onChangeText={setLastName}
            info={{ error: errors?.lastName }}
            ref={lastNameInputRef}
            onSubmitEditing={() => phoneInputRef.current?.focus()}
            returnKeyType="next"
            testID={AuthenticationE2E.Ids.Input.LastName}
          />
          <InputPhone
            placeholder="Phone Number"
            inputValue={{ phoneNumber, countryCode }}
            sardineId="phoneNumber"
            onChangeValue={({ phoneNumber }) => setPhoneNumber(phoneNumber)}
            onBlur={formatPhoneNumberToDisplayToUser}
            inputContainerStyle={{ paddingBottom: 0, marginBottom: 0 }}
            containerStyle={styles.inputStyle}
            info={{
              error:
                phoneNumber === phoneNumberUsedWhenOtpErrorReceived &&
                sendPhoneVerificationCodeError?.__typename === 'SendPhoneVerificationCodeError'
                  ? sendPhoneVerificationCodeError.message
                  : errors?.phoneNumber,
            }}
            ref={phoneInputRef}
            returnKeyType="done"
            testID={AuthenticationE2E.Ids.Input.Phone}
          />
          <PrivacyAndTerms />
          {error ? (
            <Text style={{ fontSize: 14, color: 'red', marginVertical: 10 }}>
              There was an error sending the OTP to your phone. Please check if your phone number is
              valid.
            </Text>
          ) : null}
        </View>
      </ScrollView>
      <View style={[{ marginBottom: 10 }, Platform.OS === 'web' && { marginTop: 30 }]}>
        <Button
          segmentAction={[
            'User Creates Account',
            'User Consented to Privacy Policy and Terms of Service',
          ]}
          title="Continue"
          disabled={isSubmitting}
          loading={isSubmitting}
          onPress={() => void handleSubmit()}
          testID={AuthenticationE2E.Ids.Press.SubmitSignup}
        />
      </View>
    </OnboardingWrapper>
  )
}

const styles = StyleSheet.create({
  headerText: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 32,
  },
  instructionText: {
    marginVertical: 16,
    marginBottom: 32,
    lineHeight: 24,
    color: mesaColorNames['Greyscale/400 #A4AAAC'],
  },
  inputStyle: {
    paddingHorizontal: 0,
  },
  iconStyle: {
    color: mesaColorNames['Greyscale/600 #505D64'],
  },
  linkStyle: {
    color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
  },
})
